import { nextTick, type Ref, type Reactive } from "vue";
import { useRouter } from "vue-router";
import {
  ChainInput,
  DssConnectionSettingInput,
  OperationInput,
  CreateDssConnectionSettingMutation,
  CreateChainMutation,
} from "@/generated/graphql";
import { routerPath } from "@/common/router";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import type { RadioOption } from "../types";

/** チェーン登録 ボタン操作・画面遷移に必要な依存をまとめた型。 */
interface UseChainRegisterActionsDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  dssConnectionSettingMutation: Ref<CreateDssConnectionSettingMutation | null>;
  createChainMutation: Ref<CreateChainMutation | null>;
  chainCreateInput: Reactive<ChainInput>;
  dssConnectionSettingInput: Reactive<DssConnectionSettingInput>;
  houseCardDisplayRadio: Ref<RadioOption>;
  houseCardRequiredRadio: Ref<RadioOption>;
  operatorColumnEmailRadio: Ref<RadioOption>;
  createDssConnectionSettingData: (
    input: DssConnectionSettingInput,
    logInput: OperationInput
  ) => Promise<CreateDssConnectionSettingMutation | null | undefined>;
  createChainData: (input: ChainInput) => Promise<CreateChainMutation | null | undefined>;
  resetForm: () => void;
}

/**
 * チェーン登録 ボタン操作・画面遷移を扱う composable。
 * 確認・登録・キャンセル・完了後の遷移を管理する。
 */
export const useChainRegisterActions = (deps: UseChainRegisterActionsDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    displayConfirmDialog,
    displayCompletedDialog,
    dssConnectionSettingMutation,
    createChainMutation,
    chainCreateInput,
    dssConnectionSettingInput,
    houseCardDisplayRadio,
    houseCardRequiredRadio,
    operatorColumnEmailRadio,
    createDssConnectionSettingData,
    createChainData,
    resetForm,
  } = deps;

  const router = useRouter();

  /**
   * 「登録手続きへ(確認)」ボタン @Click - チェーン登録確認ダイアログ開く。
   */
  const onClickOpenConfirmDialog = async (
    validate: () => Promise<{ valid: boolean }>
  ): Promise<void> => {
    const { valid } = await validate();
    if (valid) {
      displayConfirmDialog.value = true;
    }
  };

  /**
   * キャンセルボタン @Click - チェーン登録確認ダイアログ非表示。
   */
  const onClickConfirmCancel = (): void => {
    displayConfirmDialog.value = false;
  };

  /**
   * DSS接続設定作成（内部ヘルパー）。
   */
  const createConnectDSS = async (): Promise<void> => {
    try {
      const operationInput: OperationInput = {
        area_code: "10",
        detail: "操作詳細",
        display_category: "画面名",
        operation: "操作",
        user_id: localStorageGetUserId(),
      };
      dssConnectionSettingInput.customer_code = localStorageGetCustomerCode();
      dssConnectionSettingInput.chain_code = chainCreateInput.chain_code;
      dssConnectionSettingInput.user_id = localStorageGetUserId();

      dssConnectionSettingMutation.value = await createDssConnectionSettingData(
        dssConnectionSettingInput,
        operationInput
      ) ?? null;
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      return;
    } finally {
      overlay.value = false;
    }
  };

  /**
   * チェーン作成（内部ヘルパー）。
   */
  const createChain = async (): Promise<void> => {
    try {
      chainCreateInput.customer_code = localStorageGetCustomerCode();
      chainCreateInput.user_id = localStorageGetUserId();

      if (houseCardDisplayRadio.value.id === 0) {
        chainCreateInput.house_card_display = 0;
        chainCreateInput.house_card_required = "";
        chainCreateInput.house_card_digit = "";
        chainCreateInput.house_card_format = "";
      } else {
        chainCreateInput.house_card_display = 1;
        chainCreateInput.house_card_required =
          houseCardRequiredRadio.value.id === 0 ? "" : "required";
      }
      chainCreateInput.operator_column_email = operatorColumnEmailRadio.value.id;

      createChainMutation.value = await createChainData(chainCreateInput) ?? null;
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      return;
    } finally {
      overlay.value = false;
    }
  };

  /**
   * 確認画面：登録実行 @Click（DSS接続設定作成 → チェーン作成 → 完了画面表示）。
   */
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;
    try {
      //DSS接続設定
      await createConnectDSS();
      // チェーン作成
      await createChain();
      // チェーン登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;
      await nextTick();
      // チェーン登録完了ダイアログ 表示
      displayCompletedDialog.value = true;
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
    } finally {
      overlay.value = false;
    }
  };

  /**
   * 完了画面：戻る @Click（フォームリセット → 画面遷移）。
   */
  const onClickCompletedGoStart = async (): Promise<void> => {
    displayCompletedDialog.value = false;
    resetForm();
    await router.push({ path: routerPath.chainRegister });
  };

  return {
    onClickOpenConfirmDialog,
    onClickConfirmCancel,
    onClickConfirmEntry,
    onClickCompletedGoStart,
  };
};
